import { createSelector } from 'reselect';
import MacheteController from '../../controller';
import utils from '../../utils';

/**
 * @private
 * Retrieves the current app state of the machete.segmentation tree
 * @param {Object} state - entire redux store
 * @returns {object} current machete segmentation tree in the redux store
 */
const getSegmentation = (state) => state.machete.segmentation;

/**
 * Retrieves all nodes on the segmentation tree
 * @returns {object} object of nodes stored by uuids
 */
const getNodes = createSelector(
  getSegmentation,
  (segmentation) => segmentation.nodes,
);

/**
 * Retrieves the properties for a node with a specified uuid
 * @returns {object} of all properties for a given node
 */
const getNodeProperties = createSelector(
  (state, uuid) => uuid,
  getNodes,
  (uuid, nodes) => nodes[uuid].properties,
);

/**
 * Retrieves nodes that are of a specific type
 * @param {String} type - label for classification of the node
 * @returns {array} of nodes where nodes.type === inputType
 */
const getNodesByTypeFactory = (type) =>
  createSelector(getNodes, (nodes) =>
    Object.values(nodes).filter((n) => n.type === type),
  );

/**
 * Retrieves nodes that are of a specific type
 * @returns {array} of nodes where nodes.type === inputType
 */
const getNodesByType = createSelector(
  (state, type) => type,
  getNodes,
  (type, nodes) => Object.values(nodes).filter((n) => n.type === type),
);

/**
 * Retrieves the uuids of nodes that have been selected by the user
 * @returns {array} of node uuids
 */
const getSelectedNodeIds = createSelector(
  getSegmentation,
  (segmentation) => segmentation.selectedNodes,
);

/**
 * Retrieves the nodes who's uuids have been selected by the user
 * @returns {array} of nodes
 */
const getSelectedNodes = createSelector(
  getNodes,
  getSelectedNodeIds,
  (nodes, uuids) =>
    uuids.reduce((acc, uuid) => utils.pushConcat(acc, [nodes[uuid]]), []),
);

/**
 * Retrieves the pointer object
 * @returns {object} of pointer perimeters
 */
const getPointer = createSelector(
  getSegmentation,
  (segmentation) => segmentation.pointer,
);

/**
 * Retrieves the pointer mode
 * @returns {string} current state if the pointer
 */
const getPointerMode = createSelector(
  getPointer,
  (pointer) => pointer.config.mode,
);

/**
 * Returns nodes that are a parent or has a present value that is truthy
 * @returns {array} of nodes
 */
const getPickableNodes = createSelector(getNodes, (nodes) =>
  Object.values(nodes).filter(utils.isNodePickable),
);

/**
 * Selects nodes for Raycaster
 * @returns {array} of nodes
 */
const getObjectsForRaycaster = createSelector(
  // although we are not using getNodes, don't delete because for optimization
  // we don't want to rebuild nodes if already exist
  getPickableNodes,
  (pickableNodes) => {
    const objectsOutput = pickableNodes
      .filter((nodeObj) => {
        const node = utils.getNode(nodeObj.uuid);
        const elements = node.currentModelElements;
        const filteredElements = elements.filter(
          (element) => !!element.individualEntity,
        );

        return filteredElements.length > 0;
      })
      .reduce((acc, nodeObj) => {
        const node = utils.getNode(nodeObj.uuid);
        const elements = node.currentModelElements;
        const filteredElements = elements.filter(
          (element) => !!element.individualEntity,
        );

        const output = filteredElements.reduce(
          (result, element) =>
            utils.pushConcat(result, [
              // preserve segmentation node UUID and element for later use
              // get the THREE.Object3D needed for raycaster.intersectObjects()
              Object.assign(element.individualEntity, {
                element,
                nodeUUID: node.uuid,
              }),
            ]),
          [],
        );

        return [...acc, ...output];
      }, []);

    return objectsOutput;
  },
);

export default {
  getNodeProperties,
  getNodes,
  getNodesByType,
  getNodesByTypeFactory,
  getObjectsForRaycaster,
  getPointer,
  getPointerMode,
  getSelectedNodeIds,
  getSelectedNodes,
  getPickableNodes,
};
